import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataGrid, GridColDef, esES } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { tokens } from "@/utilities";
import careerRedirectsService, {
  CareerRedirectSummary,
} from "@/api/careerRedirects.service";

interface CareerRedirectsTableProps {
  uidInstitution?: string | number | null;
  subscriptionLevel?: number;
  userRole?: string;
}

export const CareerRedirectsTable: React.FC<CareerRedirectsTableProps> = ({
  uidInstitution,
  subscriptionLevel = 0,
  userRole = "",
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [data, setData] = useState<CareerRedirectSummary[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const totalRedirects = React.useMemo(
    () => data.reduce((sum, item) => sum + (item.redirectCount || 0), 0),
    [data]
  );

  // Validar permisos: Sólo Administrador IES o Súper Administrador Y subscriptionLevel > 2
  const isAuthorizedRole =
    userRole === "Administrador IES" || userRole === "Super Administrador";
  const hasLicense = subscriptionLevel > 2;

  useEffect(() => {
    let isMounted = true;

    async function loadRedirects() {
      if (!isAuthorizedRole || !hasLicense || !uidInstitution) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Consulta aprovechando caché de 10 minutos
        const result = await careerRedirectsService.getRedirects(
          uidInstitution,
          90
        );
        if (isMounted) {
          setData(result.summaries);
        }
      } catch (error) {
        console.error("Error al cargar redirecciones de carreras:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    void loadRedirects();

    return () => {
      isMounted = false;
    };
  }, [uidInstitution, isAuthorizedRole, hasLicense]);

  // Función para refrescar datos forzando consulta al servidor (ignora caché)
  const handleRefresh = async () => {
    if (!uidInstitution || refreshing) return;
    setRefreshing(true);
    try {
      const result = await careerRedirectsService.getRedirects(
        uidInstitution,
        90,
        true
      );
      setData(result.summaries);
    } catch (error) {
      console.error("Error al refrescar redirecciones:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Si no cumple rol o licencia, no mostrar la sección
  if (!isAuthorizedRole || !hasLicense) {
    return null;
  }

  const columns: GridColDef[] = [
    {
      field: "careerId",
      headerName: "ID Carrera",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ color: theme.palette.mode === "light" ? "#141414" : "#ffffff" }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "facultyName",
      headerName: "Facultad",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ color: theme.palette.mode === "light" ? "#292929" : "#ffffff" }}
        >
          {params.value || "Facultad no especificada"}
        </Typography>
      ),
    },
    {
      field: "careerName",
      headerName: "Nombre Carrera",
      flex: 2.5,
      minWidth: 240,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="500"
          sx={{ color: theme.palette.mode === "light" ? "#141414" : "#ffffff" }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "redirectCount",
      headerName: "Cantidad de Redirecciones",
      type: "number",
      flex: 1.5,
      minWidth: 190,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: "#007FC0",
            color: "#ffffff",
            fontWeight: "bold",
            padding: "4px 14px",
            borderRadius: "15px",
            fontSize: "13px",
            display: "inline-block",
          }}
        >
          {params.value}
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        gridColumn: "span 12",
        gridRow: { xs: "auto", md: data.length > 0 ? "span 4" : "span 2" },
        backgroundColor: colors.primary[400],
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        display: "flex",
        flexDirection: "column",
        mb: "20px",
      }}
    >
      {/* HEADER DE LA SECCIÓN CON TÍTULO Y BOTONES A LA DERECHA */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          borderBottom: `4px solid ${colors.primary[500]}`,
          p: "15px 20px",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Typography variant="h5" fontWeight="600" color={theme.palette.mode === "light" ? "#3F51B5" : colors.grey[100]}>
            Redirecciones en los últimos 90 días
          </Typography>
          <Tooltip
            title={
              <Typography variant="body2" sx={{ p: "5px" }}>
                Conteo de eventos de redirección al sitio web oficial de la
                carrera (career_redirect) registrados en Google Analytics
                durante los últimos 90 días. Se mantiene en caché por 10 minutos.
              </Typography>
            }
          >
            <InfoOutlinedIcon
              sx={{
                color: theme.palette.mode === "light" ? "#3F51B5" : colors.grey[100],
                cursor: "pointer",
                fontSize: "20px",
              }}
            />
          </Tooltip>
        </Box>

        {/* BOTONES Y CONTADORES A LA DERECHA */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="body2" color={theme.palette.mode === "light" ? "#292929" : colors.grey[200]}>
              Total programas: <strong>{data.length}</strong>
            </Typography>
            <Typography variant="body2" color={theme.palette.mode === "light" ? "#9e9e9e" : colors.grey[400]}>
              •
            </Typography>
            <Typography variant="body2" color={theme.palette.mode === "light" ? "#292929" : colors.grey[200]}>
              Total redirecciones: <strong>{totalRedirects}</strong>
            </Typography>
          </Box>

          {/* BOTÓN CON ÍCONO DE REFRESH */}
          <Tooltip title="Actualizar datos (recargar desde el servidor)">
            <span>
              <IconButton
                size="small"
                disabled={refreshing || loading}
                onClick={handleRefresh}
                sx={{
                  color: theme.palette.mode === "light" ? "#3F51B5" : "#ffffff",
                  backgroundColor: theme.palette.mode === "light" ? "#e8eaf6" : colors.primary[500],
                  p: "7px",
                  borderRadius: "5px",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "light" ? "#c5cae9" : colors.primary[600],
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: "20px",
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* BOTÓN VER DETALLES AL LADO DEL TÍTULO */}
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#3F51B5",
              color: "#ffffff",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: "600",
              padding: "6px 16px",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: colors.blueAccent[500],
              },
            }}
            onClick={() => {
              navigate("/analytics/redirects/general", {
                state: {
                  allSummaries: data,
                  uidInstitution,
                },
              });
            }}
          >
            Ver detalles
          </Button>
        </Box>
      </Box>



      {/* CONTENIDO DE LA TABLA */}
      <Box sx={{ p: "15px 20px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "180px",
              gap: "12px",
            }}
          >
            <CircularProgress sx={{ color: "#3F51B5" }} />
            <Typography variant="body1" color={colors.grey[100]}>
              Cargando redirecciones...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "120px",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="500"
              color={colors.grey[200]}
              sx={{ textAlign: "center" }}
            >
              Aún no se registran redirecciones en los últimos 90 días
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: 480,
              width: "100%",
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                color: theme.palette.mode === "light" ? "#141414" : "#fcfcfc",
                borderBottom: `1px solid ${colors.primary[500]}`,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#007FC0",
                borderBottom: "none",
                color: "#ffffff",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
                color: "#ffffff",
              },
              "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
                color: "#ffffff !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                backgroundColor: "#007FC0",
                color: "#ffffff",
              },
              "& .MuiTablePagination-root": {
                color: "#ffffff",
              },
              "& .MuiSvgIcon-root": {
                color: "#ffffff",
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.mode === "light" ? "#e8eaf6 !important" : `${colors.primary[500]} !important`,
                },
              },
            }}
          >
            <DataGrid
              rows={data}
              columns={columns}
              onRowClick={(params) => {
                const targetId = params.row.careerId || params.row.id;
                navigate(`/analytics/redirects/${encodeURIComponent(targetId)}`, {
                  state: {
                    career: params.row,
                    uidInstitution,
                  },
                });
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: "redirectCount", sort: "desc" }],
                },
              }}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20]}
              disableRowSelectionOnClick
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              getRowId={(row) => row.id}
            />
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default CareerRedirectsTable;
